import React, { useState } from "react";
import axios from "axios";
import {
  Card,
  Input,
  Button,
  Row,
  Col,
  Tabs,
  Spin,
  message,
  Empty,
  Table,
  Tag,
} from "antd";
import {
  CarOutlined,
  ToolOutlined,
  FileSearchOutlined,
  DatabaseOutlined,
} from "@ant-design/icons";
import { Container } from "reactstrap";
import Header from "components/Headers/Header";
import ProtectedRoute from "./Protected";

function HistorialVehiculo() {
  const [placa, setPlaca] = useState("");
  const [dataEquipos, setDataEquipos] = useState(null);
  const [dataRevisiones, setDataRevisiones] = useState(null);
  const [dataPorChip, setDataPorChip] = useState(null);
  const [loading, setLoading] = useState(false);

  const [idOrden, setIdOrden] = useState("");
  const [chipVehiculo, setChipVehiculo] = useState("");

  const formatearFecha = (fecha) => {
    if (!fecha) {
      return "N/A";
    }

    const fechaFormateada = new Date(fecha);

    if (isNaN(fechaFormateada.getTime())) {
      return "N/A";
    }

    return fechaFormateada.toLocaleDateString();
  };

  const consultar = async () => {
    const placaConsulta = placa.trim().toUpperCase();

    if (!placaConsulta) {
      message.warning("Por favor ingrese una placa.");
      return;
    }

    setLoading(true);
    setDataEquipos(null);
    setDataRevisiones(null);

    try {
      const [equiposRes, revisionesRes] = await Promise.all([
        axios.get(
          `https://inspecciongnvback.azurewebsites.net/api/v1/ObtenerHistorialVehiculoEquipos/${placaConsulta}`,
        ),
        axios.get(
          `https://inspecciongnvback.azurewebsites.net/api/v1/ObtenerHistorialVehiculoRevisiones/${placaConsulta}`,
        ),
      ]);

      const equipos =
        Array.isArray(equiposRes.data) && equiposRes.data.length > 0
          ? equiposRes.data[0]
          : null;

      const revisiones =
        revisionesRes.data &&
        typeof revisionesRes.data === "object" &&
        !Array.isArray(revisionesRes.data)
          ? revisionesRes.data
          : null;

      setDataEquipos(equipos);
      setDataRevisiones(revisiones);

      if (!equipos && !revisiones) {
        message.info(
          `No se encontró información para la placa ${placaConsulta}.`,
        );
      }
    } catch (error) {
      console.error("Error al consultar historial:", error);

      setDataEquipos(null);
      setDataRevisiones(null);

      message.error("Error al consultar la información del vehículo.");
    } finally {
      setLoading(false);
    }
  };

  const consultarPorChip = async () => {
    const ordenConsulta = idOrden.trim();
    const chipConsulta = chipVehiculo.trim();

    if (!ordenConsulta || !chipConsulta) {
      message.warning("Ingrese Id Orden y Chip Vehículo.");
      return;
    }

    setLoading(true);
    setDataPorChip(null);

    try {
      const res = await axios.get(
        `https://inspecciongnvback.azurewebsites.net/api/v1/ObtenerVehiculoPorChip/${ordenConsulta}/${chipConsulta}`,
      );

      const resultado =
        Array.isArray(res.data) && res.data.length > 0 ? res.data[0] : null;

      setDataPorChip(resultado);

      if (!resultado) {
        message.info(
          `No se encontró información para el chip ${chipConsulta}.`,
        );
      }
    } catch (error) {
      console.error("Error al consultar por chip:", error);

      setDataPorChip(null);

      message.error("Error al consultar por chip.");
    } finally {
      setLoading(false);
    }
  };

  const columnsEquipos = [
    {
      title: "Nombre",
      dataIndex: "nombreEquipo",
      key: "nombreEquipo",
      render: (value) => value || "-",
    },
    {
      title: "Serial",
      dataIndex: "serial",
      key: "serial",
      render: (value) => value || "-",
    },
    {
      title: "Capacidad",
      dataIndex: "capacidad",
      key: "capacidad",
      render: (value) => value || "-",
    },
    {
      title: "Fecha Fabricación",
      dataIndex: "fechaFabricacion",
      key: "fechaFabricacion",
      render: (value) => formatearFecha(value),
    },
    {
      title: "Fecha Próx. Prueba",
      dataIndex: "prueba",
      key: "prueba",
      render: (value) => formatearFecha(value),
    },
    {
      title: "Última Prueba",
      dataIndex: "ultimaPrueba",
      key: "ultimaPrueba",
      render: (value) => formatearFecha(value),
    },
    {
      title: "Fecha Instalación",
      dataIndex: "fechaInstalacion",
      key: "fechaInstalacion",
      render: (value) => formatearFecha(value),
    },
  ];

  const columnsRevisiones = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render: (value) => value || "-",
    },
    {
      title: "Fecha Creación",
      dataIndex: "fechaCreacion",
      key: "fechaCreacion",
      render: (value) => formatearFecha(value),
    },
    {
      title: "Fecha Prestación",
      dataIndex: "fechaPrestacionServicio",
      key: "fechaPrestacionServicio",
      render: (value) => formatearFecha(value),
    },
    {
      title: "Fecha Conversión",
      dataIndex: "fechaConversion",
      key: "fechaConversion",
      render: (value) => formatearFecha(value),
    },
    {
      title: "Tipo Gas",
      dataIndex: "tipoGas",
      key: "tipoGas",
      render: (value) => value || "-",
    },
    {
      title: "Tipo Orden",
      dataIndex: "nombreTipoOrdenServicio",
      key: "nombreTipoOrdenServicio",
      render: (value) => value || "-",
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      render: (estado) => (
        <Tag color={estado === "Presentado" ? "blue" : "green"}>
          {estado || "N/A"}
        </Tag>
      ),
    },
    {
      title: "Chip Vehículo",
      dataIndex: "chipVehiculo",
      key: "chipVehiculo",
      render: (value) => value || "-",
    },
    {
      title: "Taller",
      dataIndex: "nombreTaller",
      key: "nombreTaller",
      render: (value) => value || "-",
    },
    {
      title: "Inspector",
      dataIndex: "nombreInspector",
      key: "nombreInspector",
      render: (value) => value || "N/A",
    },
    {
      title: "Próx. Revisión",
      dataIndex: "fechaProxRevision",
      key: "fechaProxRevision",
      render: (value) => formatearFecha(value),
    },
  ];

  const tieneEquipos = dataEquipos && typeof dataEquipos === "object";

  const tieneRevisiones = dataRevisiones && typeof dataRevisiones === "object";

  const tieneResultadoPlaca = tieneEquipos || tieneRevisiones;

  return (
    <ProtectedRoute>
      <Header />

      <Container className="mt--7" fluid>
        <Card title="Consulta Historial Vehículo">
          <Tabs defaultActiveKey="1">
            <Tabs.TabPane
              tab={
                <span>
                  <FileSearchOutlined /> Por Placa
                </span>
              }
              key="1"
            >
              <Row gutter={12} style={{ marginBottom: 16 }}>
                <Col flex="auto">
                  <Input
                    placeholder="Ingrese la placa"
                    value={placa}
                    maxLength={10}
                    onChange={(e) => setPlaca(e.target.value.toUpperCase())}
                    onPressEnter={consultar}
                    disabled={loading}
                  />
                </Col>

                <Col>
                  <Button type="primary" onClick={consultar} loading={loading}>
                    Consultar
                  </Button>
                </Col>
              </Row>

              {loading && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "30px",
                  }}
                >
                  <Spin size="large" />
                </div>
              )}

              {!loading && tieneResultadoPlaca && (
                <Tabs defaultActiveKey="1">
                  <Tabs.TabPane
                    tab={
                      <span>
                        <ToolOutlined /> Equipos
                      </span>
                    }
                    key="1"
                  >
                    {tieneEquipos ? (
                      <Card>
                        <h3>
                          {dataEquipos?.vehiculo?.placa || placa || "Vehículo"}{" "}
                          - {dataEquipos?.marca || "Marca no disponible"}
                        </h3>

                        <Table
                          dataSource={
                            Array.isArray(dataEquipos?.equipos)
                              ? dataEquipos.equipos
                              : []
                          }
                          columns={columnsEquipos}
                          rowKey={(record) =>
                            record.id || record.serial || Math.random()
                          }
                          pagination={false}
                          bordered
                          locale={{
                            emptyText: (
                              <Empty description="No hay equipos registrados" />
                            ),
                          }}
                        />
                      </Card>
                    ) : (
                      <Empty description="No hay equipos registrados" />
                    )}
                  </Tabs.TabPane>

                  <Tabs.TabPane
                    tab={
                      <span>
                        <CarOutlined /> Revisiones
                      </span>
                    }
                    key="2"
                  >
                    {tieneRevisiones ? (
                      <Card>
                        <h3>
                          {dataRevisiones?.vehiculo?.placa ||
                            placa ||
                            "Vehículo"}{" "}
                          - {dataRevisiones?.marca || "Marca no disponible"}
                        </h3>

                        <p>
                          <b>Próxima revisión:</b>{" "}
                          {formatearFecha(dataRevisiones?.fechaProxRevision)}
                        </p>

                        <Table
                          dataSource={
                            Array.isArray(dataRevisiones?.revisiones)
                              ? dataRevisiones.revisiones
                              : []
                          }
                          columns={columnsRevisiones}
                          rowKey={(record) => record.id || Math.random()}
                          pagination={false}
                          bordered
                          scroll={{ x: "max-content" }}
                          locale={{
                            emptyText: (
                              <Empty description="No hay revisiones registradas" />
                            ),
                          }}
                        />
                      </Card>
                    ) : (
                      <Empty description="No hay revisiones registradas" />
                    )}
                  </Tabs.TabPane>
                </Tabs>
              )}

              {!loading && !tieneResultadoPlaca && (
                <Empty description="Realice una consulta por placa para visualizar el historial." />
              )}
            </Tabs.TabPane>

            <Tabs.TabPane
              tab={
                <span>
                  <DatabaseOutlined /> Por Chip
                </span>
              }
              key="2"
            >
              <Row gutter={12} style={{ marginBottom: 16 }}>
                <Col>
                  <Input
                    placeholder="Id Orden"
                    value={idOrden}
                    onChange={(e) => setIdOrden(e.target.value)}
                    onPressEnter={consultarPorChip}
                    disabled={loading}
                  />
                </Col>

                <Col>
                  <Input
                    placeholder="Chip Vehículo"
                    value={chipVehiculo}
                    onChange={(e) => setChipVehiculo(e.target.value)}
                    onPressEnter={consultarPorChip}
                    disabled={loading}
                  />
                </Col>

                <Col>
                  <Button
                    type="primary"
                    onClick={consultarPorChip}
                    loading={loading}
                  >
                    Consultar
                  </Button>
                </Col>
              </Row>

              {loading && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "30px",
                  }}
                >
                  <Spin size="large" />
                </div>
              )}

              {!loading && dataPorChip && (
                <Card>
                  <h3>
                    {dataPorChip?.vehiculo?.placa || "Placa no disponible"} -{" "}
                    {dataPorChip?.marca || "Marca no disponible"}
                  </h3>

                  <Table
                    dataSource={
                      Array.isArray(dataPorChip?.equipos)
                        ? dataPorChip.equipos
                        : []
                    }
                    columns={columnsEquipos}
                    rowKey={(record) =>
                      record.id || record.serial || Math.random()
                    }
                    pagination={false}
                    bordered
                    locale={{
                      emptyText: (
                        <Empty description="No hay equipos registrados" />
                      ),
                    }}
                  />
                </Card>
              )}

              {!loading && !dataPorChip && (
                <Empty description="Realice una consulta por chip para visualizar la información." />
              )}
            </Tabs.TabPane>
          </Tabs>
        </Card>
      </Container>
    </ProtectedRoute>
  );
}

export default HistorialVehiculo;
